import { Component, OnInit } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
// import { filter, switchMap } from 'rxjs/operators'; // Not needed if SwUpdate is removed
import { RouterModule } from '@angular/router';
import { KeycloakAngularModule } from 'keycloak-angular';
import { GooglePayService } from "./shared/services/google-pay.service";
// import { SwUpdate } from '@angular/service-worker'; // Removed SwUpdate import
import { LayoutService } from './layout/service/layout-service.service';

import { LoadingSpinnerComponent } from './shared/components/loader/loading-spinner.component';
import { ManifestService } from './services/manifest.service';
import { environment } from '@env';
import {
  hasImpersonationLaunchTokensInUrl,
  hasStoredImpersonationSession,
  shouldBlockImpersonationFallback,
} from '@apruv-client/services';

@Component({
  standalone: true,
  imports: [
    RouterModule,
    KeycloakAngularModule,
    FormsModule,
    ReactiveFormsModule,
    LoadingSpinnerComponent
],
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  title = 'web';
  // showUpdateAvailable = false; // Removed as SwUpdate logic that set this is gone
  showResetButton = false; // Show reset app button
  private bootstrapTimeout: any;

  private logDebug(...args: any[]): void {
    if (!environment.production) {
      console.log(...args);
    }
  }

  constructor(
    private googlePayService: GooglePayService,
    // private swUpdate: SwUpdate, // Removed SwUpdate injection
    private layoutService: LayoutService,
    private manifestService: ManifestService
  ) {
    // Show reset button if Angular doesn't bootstrap in 5 seconds
    this.bootstrapTimeout = setTimeout(() => {
      this.showResetButton = true;
    }, 5000);
  }

  ngOnInit() {
    this.logDebug('🚀 AppComponent.ngOnInit() started');
    // Clear the bootstrap timeout if Angular bootstrapped in time
    if (this.bootstrapTimeout) {
      clearTimeout(this.bootstrapTimeout);
      this.bootstrapTimeout = null;
    }
    // Initialize manifest handling
    this.manifestService.initializeManifest();
    // this.initServiceWorkerUpdates(); // Removed call to SwUpdate logic
    const hardRefreshTriggered = this.checkLastLoadTime();
    if (hardRefreshTriggered) {
      this.logDebug('⏹️ App bootstrap halted because hard refresh was triggered');
      return;
    }
    this.setupPwaHandling();
    // Ensure theme is set on every app start
    this.layoutService.toggleLightDark(this.layoutService.getActivTheme());

    // Show reset button in PWA mode
    if (this.isRunningAsPWA()) {
      this.showResetButton = true;
    }

    // Auto-detect if SW is not controlling the page and auto-reset
    // This specific check for navigator.serviceWorker.controller might be less relevant
    // or could be adapted if you have other PWA consistency checks.
    // For now, keeping it as it was, but its effectiveness changes without a SW being registered.
    if ('serviceWorker' in navigator && this.isRunningAsPWA()) {
      setTimeout(() => {
        if (navigator.serviceWorker.controller) {
          // If there's still a controller, it might be a leftover one we want to get rid of.
          // The unregistration script in index.html is the primary mechanism for this now.
          // Consider if this specific hardRefresh is still needed or could cause reload loops.
          // For now, commenting it out to prevent potential issues if an old SW is lingering.
          // this.hardRefresh();
        } else {
          // No controller, which is expected now.
        }
      }, 2000); // Wait 2 seconds after load
    }
    
    this.logDebug('✅ AppComponent.ngOnInit() completed');
  }

  // private initServiceWorkerUpdates(): void { // Entire method removed as it relies on SwUpdate
  //   if (!this.swUpdate.isEnabled) {
  //     return;
  //   }
  //   this.swUpdate.versionUpdates
  //     .pipe(
  //       filter(evt => evt.type === 'VERSION_READY')
  //     )
  //     .subscribe(() => {
  //       this.showUpdateAvailable = true;
  //     });
  //   this.swUpdate.unrecoverable.subscribe(async evt => {
  //     console.error('SW unrecoverable:', evt.reason);
  //     const regs = await navigator.serviceWorker.getRegistrations();
  //     await Promise.all(regs.map(r => r.unregister()));
  //     location.reload();
  //   });
  //   setInterval(() => {
  //     this.swUpdate.checkForUpdate()
  //       .catch(err => console.error('Error checking for updates', err));
  //   }, 60 * 60 * 1000);
  // }

  private async hardRefresh(): Promise<void> {
    (window as any).__APRUV_HARD_REFRESH_IN_PROGRESS__ = true;

    /* wipe SW + caches */
    // The unregistration script in index.html is the primary mechanism for SW unregistration.
    // This can still attempt to unregister if needed, or focus only on caches & storage.
    if ('serviceWorker' in navigator) {
      try {
        const regs = await navigator.serviceWorker.getRegistrations();
        await Promise.all(regs.map(r => r.unregister()));
        console.log('Service workers unregistered via hardRefresh.');
      } catch (e) {
        console.error('Error unregistering service workers via hardRefresh:', e);
      }
    }
    
    if (typeof caches !== 'undefined'){
        try {
            await caches.keys().then(keys => Promise.all(keys.map(k => caches.delete(k))));
            console.log('Caches cleared via hardRefresh.');
        } catch (e) {
            console.error('Error clearing caches via hardRefresh:', e);
        }
    }

    /* wipe storage */
    try {
        localStorage.clear();
        sessionStorage.clear();
        console.log('Storage cleared via hardRefresh.');
    } catch (e) {
        console.error('Error clearing storage via hardRefresh:', e);
    }

    /* reload */
    location.reload(); // Consider location.reload(true) for a harder reload, but can be aggressive.
  }

  private checkLastLoadTime(): boolean {
    try {
      if (this.hasImpersonationHandoffState()) {
        localStorage.setItem('app_last_load', String(Date.now()));
        return false;
      }

      const last = Number(localStorage.getItem('app_last_load'));
      if (last && Date.now() - last > 24 * 60 * 60 * 1000) {
        console.log('>24 h since last load – doing a hard refresh');
        void this.hardRefresh();
        return true;
      }
      localStorage.setItem('app_last_load', String(Date.now()));
    } catch (error) {
      console.warn('Unable to access storage during last-load check; skipping hard refresh guard.', error);
    }

    return false;
  }

  private hasImpersonationHandoffState(): boolean {
    return (
      hasImpersonationLaunchTokensInUrl() ||
      hasStoredImpersonationSession() ||
      shouldBlockImpersonationFallback()
    );
  }

  private isRunningAsPWA(): boolean {
    return window.matchMedia('(display-mode: standalone)').matches ||
      // @ts-ignore: Property 'standalone' may not exist on 'Navigator'
      (window.navigator && (window.navigator as any).standalone) || // Added null check for window.navigator
      document.referrer.includes('android-app://');
  }

  private setupPwaHandling(): void {
    // PWA-specific initializations
    if (this.isRunningAsPWA()) {
      // Add this as a data attribute to enable PWA-specific CSS
      document.body.setAttribute('data-pwa-mode', 'true');

      // The PWA-specific logic below might need review if it relied on SW updates.
      // Forcing an update check is no longer relevant.
      // The network activity tracker and stale check might still be useful for other reasons.

      // Force service worker update check on PWA launch - REMOVED as no SW registered
      // if ('serviceWorker' in navigator) {
      //   navigator.serviceWorker.ready.then(registration => {
      //     registration.update();
      //   });
      // }

      // Add reload logic if no network activity for 5 minutes (common iOS PWA issue)
      let lastUserActivity = Date.now();
      const userActivityTracker = () => {
        lastUserActivity = Date.now();
      };

      // Track network activity
      try {
        window.addEventListener('fetch', userActivityTracker);
      } catch (error) {
        console.warn('Fetch event listener not supported in this browser');
      }
      window.addEventListener('online', userActivityTracker);

      // Also track user activity to prevent reload if user is interacting
      const userActivityEvents = [
        'mousemove',
        'keydown',
        'touchstart',
        'scroll',
        'click'
      ];
      userActivityEvents.forEach(event => {
        window.addEventListener(event, userActivityTracker, { passive: true });
      });

      // Track camera/mic usage as activity
      if (navigator.mediaDevices && navigator.mediaDevices.addEventListener) {
        navigator.mediaDevices.addEventListener('devicechange', async () => {
          try {
            const devices = await navigator.mediaDevices.enumerateDevices();
            const isMediaActive = devices.some(
              d => (d.kind === 'videoinput' || d.kind === 'audioinput')
            );
            if (isMediaActive) {
              console.log('Camera or microphone device change detected, resetting timer');
              userActivityTracker();
            }
          } catch (e) {
          }
        });
      }

      // Track when user returns to the app/tab
      document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
          userActivityTracker();
        }
      });

      // Check and reload if stale
      setInterval(() => {
        const minutesSinceActivity = (Date.now() - lastUserActivity) / (1000 * 60);
        if (minutesSinceActivity > 5) {
          window.location.reload();
        }
      }, 30000); // Check every 30 seconds
    }
  }

  // onReloadForUpdate() { // Removed as it relies on SwUpdate
  //   this.swUpdate.activateUpdate().then(() => document.location.reload());
  // }

  onResetApp() {
    this.hardRefresh();
  }
}
